import { useCallback } from "react"
import axios from "axios"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { useAsyncCapable } from "hooks/core/useAsyncCapable"
import { getAssistantStatus, postAssistantTurn } from "api/AI/assistant"
import { useAssistantStore } from "./assistantStore"
import {
  ASSISTANT_ERROR_CODES,
  AssistantErrorCode,
  AssistantTurnRequest,
  AssistantTurnResponse,
  Proposal,
  PROPOSAL_KINDS,
} from "./types"

export const ASSISTANT_STATUS_KEY = ["assistant-status"] as const

export const UNAVAILABLE_TEXT = "The assistant is unavailable right now"
export const TOKEN_EXPIRED_TEXT = "Your session expired, please try again"
export const DISABLED_TEXT = "The assistant is not switched on for your organisation"

const UNAVAILABLE: AssistantTurnResponse = {
  success: false,
  code: "error",
  message: UNAVAILABLE_TEXT,
}

const TOKEN_EXPIRED: AssistantTurnResponse = { success: false, code: "token-expired" }

/** What `useAuthedAxios` throws when the user's Kinde token can't be read. */
const NO_TOKEN_MESSAGE = "Authentication token not available"

/**
 * The user's sign-in is gone: the token couldn't be read at send time, or a
 * call (the turn or its polling) came back 401.
 */
const isSignInFailure = (e: unknown): boolean =>
  axios.isAxiosError(e)
    ? e.response?.status === 401
    : e instanceof Error && e.message === NO_TOKEN_MESSAGE

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v)

/** A JSON body that arrived as text is parsed; anything else is returned as it is. */
const parseBody = (body: unknown): unknown => {
  if (typeof body !== "string") return body
  try {
    return JSON.parse(body)
  } catch {
    return body
  }
}

/** A proposal of a known kind with a check result; a missing errors list becomes empty. */
const toProposal = (raw: unknown): Proposal | null => {
  if (!isRecord(raw) || typeof raw.id !== "string") return null
  if (!PROPOSAL_KINDS.includes(raw.kind as Proposal["kind"])) return null
  const v = raw.validation
  if (!isRecord(v) || typeof v.ok !== "boolean") return null
  return {
    ...raw,
    validation: {
      ok: v.ok,
      errors: Array.isArray(v.errors) ? v.errors : [],
      ...(v.unchecked === true ? { unchecked: true as const } : {}),
    },
  } as Proposal
}

/**
 * Reads a turn's body as an `AssistantTurnResponse`. The Conductor can send
 * plain text or HTML on a failure (the converter down, an unknown route)
 * while calling it JSON, so a body that is not a response is the "unavailable"
 * error. Proposals of unknown kinds are dropped.
 */
export const toTurnResponse = (body: unknown): AssistantTurnResponse => {
  const raw = parseBody(body)
  if (!isRecord(raw) || typeof raw.success !== "boolean") return UNAVAILABLE
  const code =
    raw.code === undefined
      ? undefined
      : ASSISTANT_ERROR_CODES.includes(raw.code as AssistantErrorCode)
        ? (raw.code as AssistantErrorCode)
        : "error"
  const message = typeof raw.message === "string" ? raw.message : undefined
  if (!raw.success) return { success: false, code: code ?? "error", message }
  const data = raw.data
  if (!isRecord(data) || typeof data.reply !== "string") return UNAVAILABLE
  const proposals = Array.isArray(data.proposals)
    ? data.proposals.map(toProposal).filter((p): p is Proposal => p !== null)
    : []
  return {
    success: true,
    code,
    message,
    data: {
      reply: data.reply,
      proposals,
      rounds: typeof data.rounds === "number" ? data.rounds : 0,
    },
  }
}

/** The text the panel shows for a turn that failed. */
export const turnErrorText = (res: AssistantTurnResponse): string => {
  switch (res.code) {
    case "token-expired":
      return TOKEN_EXPIRED_TEXT
    case "disabled":
      return DISABLED_TEXT
    default:
      return res.message || UNAVAILABLE_TEXT
  }
}

/**
 * Whether the user's organisation has the assistant (`false` while it is
 * being asked, and when the question fails).
 */
export const useAssistantStatus = (): { enabled: boolean; isFetched: boolean } => {
  const getAxios = useAuthedAxios()
  const query = useQuery({
    queryKey: ASSISTANT_STATUS_KEY,
    queryFn: async (): Promise<boolean> => {
      const body = parseBody((await getAssistantStatus(await getAxios())).data)
      return isRecord(body) && body.enabled === true
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
  return { enabled: query.data === true, isFetched: query.isFetched }
}

/**
 * Sends a turn and waits for its answer (a 202 is polled to the end). The
 * promise always resolves to an `AssistantTurnResponse`: a token that can't
 * be read or a 401 is `token-expired`, another error status with a response
 * body (a 403 `disabled` from the first call or from `/async-get`) is read
 * like a 200, and anything else is "unavailable".
 * A `disabled` answer closes the panel and asks for the status again, so
 * the toggle goes away.
 */
export const useAssistantTurn = (): {
  sendTurn: (req: AssistantTurnRequest) => Promise<AssistantTurnResponse>
  isSending: boolean
} => {
  const getAxios = useAuthedAxios()
  const { awaitAsyncCapable } = useAsyncCapable()
  const client = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (req: AssistantTurnRequest): Promise<AssistantTurnResponse> => {
      let res: AssistantTurnResponse
      try {
        const first = await postAssistantTurn(await getAxios(), req)
        res = toTurnResponse(await awaitAsyncCapable<unknown>(first))
      } catch (e) {
        res = isSignInFailure(e)
          ? TOKEN_EXPIRED
          : axios.isAxiosError(e) && e.response
            ? toTurnResponse(e.response.data)
            : UNAVAILABLE
      }
      if (res.code === "disabled") {
        useAssistantStore.getState().setOpen(req.projectId, false)
        void client.invalidateQueries({ queryKey: ASSISTANT_STATUS_KEY })
      }
      return res
    },
  })

  const { mutateAsync } = mutation
  const sendTurn = useCallback(
    (req: AssistantTurnRequest) => mutateAsync(req),
    [mutateAsync]
  )
  return { sendTurn, isSending: mutation.isPending }
}
